import type { HelpTopicDetail } from './help-topic-detail.types';

function detail(
  partial: Omit<HelpTopicDetail, 'highlights' | 'steps' | 'sections' | 'tips' | 'relatedTopicIds'> &
    Partial<Pick<HelpTopicDetail, 'highlights' | 'steps' | 'sections' | 'tips' | 'relatedTopicIds'>>,
): HelpTopicDetail {
  return {
    highlights: partial.highlights ?? [],
    steps: partial.steps ?? [],
    sections: (partial.sections ?? []).map((section) => ({
      ...section,
      paragraphs: section.paragraphs ?? [],
    })),
    tips: partial.tips ?? [],
    relatedTopicIds: partial.relatedTopicIds ?? [],
    summary: partial.summary,
    resources: partial.resources,
    quiz: partial.quiz,
  };
}

/** Fiches détaillées du centre d’aide LyrraOne — contenu modale complet. */
export const LYRRAONE_HELP_TOPIC_DETAILS: Record<string, HelpTopicDetail> = {
  'demarrage-tableau-bord': detail({
    summary:
      'Le tableau de bord LyrraOne regroupe vos indicateurs formateur : formations actives, ventes récentes, contacts et raccourcis vers le marketing.',
    highlights: [
      { label: 'Accès', value: 'Menu Pilotage → Tableau de bord' },
      { label: 'Public', value: 'Formateurs LyrraOne' },
      { label: 'Fréquence', value: 'Consultation hebdomadaire recommandée' },
    ],
    steps: [
      {
        title: 'Ouvrir le tableau de bord',
        description: 'Cliquez sur Tableau de bord dans le menu latéral (section Pilotage).',
      },
      {
        title: 'Lire les cartes récapitulatives',
        description:
          'Formations publiées, contacts récents, ventes Store et activité marketing s’affichent en un coup d’œil.',
      },
      {
        title: 'Utiliser les raccourcis',
        description:
          'Chaque carte ou bouton d’action vous redirige vers la section concernée (formations, contacts, analytiques…).',
      },
      {
        title: 'Repérer les alertes',
        description:
          'Les bannières de quota (plan, e-mails, SMS) et les actions en attente apparaissent en haut de page.',
      },
    ],
    sections: [
      {
        heading: 'Ce que vous y trouvez',
        bullets: [
          'Nombre de formations publiées et brouillons',
          'Ventes et inscriptions récentes sur le Store',
          'Contacts ajoutés ou importés',
          'Raccourcis vers campagnes e-mail et entonnoirs',
        ],
      },
      {
        heading: 'Bonnes pratiques',
        paragraphs: [
          'Consultez le tableau de bord en début de semaine pour prioriser vos actions commerciales et pédagogiques.',
        ],
      },
    ],
    tips: [
      'Épinglez les modules que vous utilisez le plus via la recherche globale (⌘K).',
      'Comparez l’évolution des ventes avec le tableau de bord analytique pour affiner votre stratégie.',
    ],
    relatedTopicIds: ['demarrage-navigation', 'lo-analytics-dashboard'],
  }),

  'demarrage-navigation': detail({
    summary:
      'LyrraOne organise le menu en quatre zones : Création, Marketing, Pilotage et Médias — adaptées au formateur indépendant.',
    highlights: [
      { label: 'Création', value: 'Formations, podcasts' },
      { label: 'Marketing', value: 'Audience, vente, site' },
      { label: 'Pilotage', value: 'Dashboard, apprenants, stats' },
    ],
    steps: [
      {
        title: 'Section Création',
        description: 'Formations simples et podcasts : tout ce que vous produisez pédagogiquement.',
      },
      {
        title: 'Section Marketing',
        description:
          'Contacts, e-mails, SMS, automatisations, entonnoirs, site, page créateur, CRM, affiliation, RDV, communautés, webinaires et produits physiques.',
      },
      {
        title: 'Section Pilotage',
        description: 'Tableau de bord formateur, espace apprenant (vue formateur) et analytiques.',
      },
      {
        title: 'Section Médias',
        description: 'Bibliothèque multimédia partagée entre formations, e-mails et site.',
      },
    ],
    sections: [
      {
        heading: 'Navigation rapide',
        bullets: [
          'Recherche globale (⌘K ou Ctrl+K) : accès direct à une page',
          'Fil d’Ariane en haut : retour contextuel',
          'Centre d’aide (/help) : fiches par module',
        ],
      },
    ],
    tips: [
      'Les entrées grisées ou absentes indiquent une fonctionnalité non incluse dans votre plan actuel.',
      'Sur mobile, le menu latéral se replie : utilisez l’icône hamburger en haut à gauche.',
    ],
    relatedTopicIds: ['demarrage-tableau-bord', 'demarrage-profil'],
  }),

  'demarrage-profil': detail({
    summary:
      'Votre profil regroupe identité, langue, notifications, abonnement, clés API et paramètres de compte LyrraOne.',
    steps: [
      {
        title: 'Ouvrir Paramètres',
        description: 'Cliquez sur votre avatar en haut à droite ou sur Paramètres dans le menu.',
      },
      {
        title: 'Mettre à jour vos informations',
        description:
          'Nom, e-mail et photo apparaissent sur vos pages publiques, e-mails transactionnels et factures.',
      },
      {
        title: 'Choisir la langue',
        description: 'L’interface et les e-mails transactionnels suivent la langue sélectionnée.',
      },
      {
        title: 'Gérer l’abonnement',
        description: 'Consultez votre plan, vos quotas et passez au plan supérieur depuis Abonnement & factures.',
      },
    ],
    sections: [
      {
        heading: 'Rubriques utiles',
        bullets: [
          'Profil & identité',
          'Abonnement & factures (Stripe portal)',
          'Clés API (intégrations MCP, automatisations)',
          'Notifications et sécurité (mot de passe)',
        ],
      },
    ],
    tips: [
      'Utilisez une photo professionnelle : elle s’affiche sur votre site et votre page créateur.',
      'Vérifiez l’adresse e-mail de facturation pour recevoir les reçus Stripe.',
    ],
    relatedTopicIds: ['lo-plans', 'demarrage-tableau-bord'],
  }),

  'lo-plans': detail({
    summary:
      'Chaque plan LyrraOne définit des limites (contacts, envois e-mail/SMS, automatisations, stockage). Passez au plan supérieur depuis Profil → Abonnement & factures.',
    sections: [
      {
        heading: 'Formules',
        bullets: [
          'Formations illimitées (tous les plans)',
          'Apprenants illimités (tous les plans)',
          'CRM illimité (tous les plans)',
          'Funnels illimités (tous les plans)',
          'Modèles & thèmes site (19+) sur tous les plans',
          'Gratuit sans abonnement (commission LyrraPay : 12 %)',
          'Créateur — 29 € / mois : LyrraPay (6 %) ou votre Stripe (0 %), au choix',
          'Expert — 99 € / mois : votre compte Stripe obligatoire (0 % de commission LyrraOne)',
        ],
      },
      {
        heading: 'Quotas à surveiller',
        bullets: [
          'Volume d’envois e-mail / SMS mensuels',
          'Nombre d’automatisations actives (3 en Gratuit)',
          'Stockage médias selon le plan',
          'Nombre de communautés et webinaires actifs',
        ],
      },
      {
        heading: 'Passer au plan supérieur',
        paragraphs: [
          'Les bannières de quota sur le tableau de bord et dans les modules concernés proposent un lien direct vers la mise à niveau.',
        ],
      },
    ],
    tips: [
      'Le plan Créateur convient si vous démarrez la monétisation avec LyrraPay sans configurer Stripe.',
      'Le plan Expert est recommandé dès que vous avez un compte Stripe actif et un volume de ventes régulier.',
    ],
    relatedTopicIds: ['lo-formation-store', 'lo-email'],
  }),

  'lo-formation-creer': detail({
    summary:
      'Créez une formation simple en quelques clics : titre, description puis éditeur par chapitres sans canvas graphique.',
    steps: [
      { title: 'Menu Création → Formations', description: 'Ouvrez la liste de vos parcours.' },
      {
        title: 'Nouvelle formation',
        description: 'Cliquez sur Créer, donnez un titre clair et une description courte (visible Store).',
      },
      {
        title: 'Choisir le mode simple',
        description: 'LyrraOne utilise l’éditeur simple par défaut : modules et chapitres linéaires.',
      },
      {
        title: 'Premier chapitre',
        description: 'Ajoutez un chapitre d’introduction pour accueillir l’apprenant et expliquer le parcours.',
      },
    ],
    sections: [
      {
        heading: 'Après la création',
        bullets: [
          'Structurez en modules depuis la barre latérale',
          'Prévisualisez le player à tout moment',
          'Publiez sur le Store quand le contenu minimum est prêt',
        ],
      },
    ],
    tips: ['Commencez par un plan de 3 modules avant de rédiger le détail — l’IA peut vous aider.'],
    relatedTopicIds: ['lo-formation-structure', 'lo-formation-contenu'],
  }),

  'lo-formation-structure': detail({
    summary:
      'Structurez en modules (thèmes) et chapitres (leçons). L’apprenant avance chapitre par chapitre dans un ordre linéaire.',
    steps: [
      {
        title: 'Créer des modules',
        description: 'Chaque module regroupe un thème (ex. « Module 1 — Les fondamentaux »).',
      },
      {
        title: 'Ajouter des chapitres',
        description: 'Un chapitre = une leçon consommable en 5 à 15 minutes.',
      },
      {
        title: 'Réordonner',
        description: 'Glissez-déposez modules et chapitres pour ajuster la progression.',
      },
      {
        title: 'Prévisualiser',
        description: 'Ouvrez le player en mode prévisualisation pour valider la navigation.',
      },
    ],
    sections: [
      {
        heading: 'Règles de structuration',
        bullets: [
          '1 module = une grande partie du programme',
          '5 à 8 chapitres par module pour garder la lisibilité',
          'Terminez chaque module par un quiz ou une synthèse',
        ],
      },
    ],
    tips: [
      'Un module = une grande partie ; un chapitre = une leçon courte et actionnable.',
      'Numérotez vos modules dans le titre pour guider l’apprenant.',
    ],
    relatedTopicIds: ['lo-formation-contenu', 'lo-learner-parcours'],
  }),

  'lo-formation-contenu': detail({
    summary:
      'Chaque chapitre accepte texte enrichi, vidéo, audio, quiz, devoirs et pièces jointes — idéal pour un parcours linéaire.',
    steps: [
      {
        title: 'Ouvrir un chapitre',
        description: 'Cliquez sur le chapitre dans la barre latérale de l’éditeur.',
      },
      {
        title: 'Ajouter des blocs',
        description: 'Texte, vidéo, audio, image, quiz, liens web ou devoir selon votre pédagogie.',
      },
      {
        title: 'Enrichir le texte',
        description: 'Titres, listes, citations et mise en forme pour faciliter la lecture.',
      },
      {
        title: 'Tester le rendu',
        description: 'Prévisualisez sur desktop et mobile avant publication.',
      },
    ],
    sections: [
      {
        heading: 'Types de contenu',
        bullets: [
          'Texte et titres structurés',
          'Vidéo (upload bibliothèque ou lien externe)',
          'Audio / podcast intégré',
          'Quiz QCM avec correction',
          'Devoirs (upload fichier apprenant)',
          'Documents PDF et liens web',
        ],
      },
    ],
    tips: [
      'Réutilisez les médias de la bibliothèque pour éviter les doublons.',
      'Alternez texte, vidéo et quiz pour maintenir l’engagement.',
    ],
    relatedTopicIds: ['medias-bibliotheque', 'lo-formation-ia'],
  }),

  'lo-formation-ia': detail({
    summary:
      'L’assistant IA propose des plans de module, du texte pédagogique ou des questions de quiz. Relisez et adaptez toujours avant publication.',
    steps: [
      {
        title: 'Ouvrir l’assistant',
        description: 'Dans l’éditeur, ouvrez le panneau IA (icône assistant).',
      },
      {
        title: 'Décrire votre besoin',
        description: 'Précisez le public, le niveau, la durée et le ton souhaité.',
      },
      {
        title: 'Insérer le résultat',
        description: 'Validez le contenu généré bloc par bloc dans votre chapitre.',
      },
      {
        title: 'Relire et personnaliser',
        description: 'Ajoutez vos exemples, votre voix et vos références métier.',
      },
    ],
    sections: [
      {
        heading: 'Cas d’usage',
        bullets: [
          'Plan de formation complet',
          'Rédaction de chapitre ou synthèse',
          'Génération de questions QCM',
          'Reformulation ou simplification de texte',
        ],
      },
    ],
    tips: [
      'Précisez le public cible et le niveau pour de meilleurs résultats.',
      'L’IA accélère la production mais ne remplace pas votre expertise métier.',
    ],
    relatedTopicIds: ['lo-formation-contenu', 'lo-formation-structure'],
  }),

  'lo-formation-store': detail({
    summary:
      'Publiez sur le Store LyrraOne pour vendre ou offrir votre formation. Configurez prix, visuel et fiche produit.',
    steps: [
      { title: 'Depuis la formation', description: 'Ouvrez Paramètres → Publication / Store.' },
      {
        title: 'Fiche produit',
        description: 'Ajoutez image de couverture, pitch commercial et description longue.',
      },
      {
        title: 'Tarification',
        description: 'Fixez un prix, proposez un accès gratuit ou une promotion limitée.',
      },
      {
        title: 'Publier',
        description: 'Activez la visibilité Store : la formation apparaît sur votre vitrine publique.',
      },
    ],
    sections: [
      {
        heading: 'Checklist avant publication',
        bullets: [
          'Au moins un module complet avec contenu',
          'Image de couverture professionnelle',
          'Description orientée bénéfices pour l’acheteur',
          'Paiement configuré (LyrraPay ou Stripe selon plan)',
        ],
      },
    ],
    tips: ['Testez l’achat en navigation privée pour valider le parcours complet.'],
    relatedTopicIds: ['lo-store-vitrine', 'lo-store-prix'],
  }),

  'lo-formation-gamification': detail({
    summary:
      'Motivez vos apprenants avec XP, niveaux, bonus de rapidité et classement — configurable par formation.',
    steps: [
      {
        title: 'Ouvrir les paramètres de formation',
        description: 'Depuis l’éditeur, accédez à Paramètres → Gamification.',
      },
      {
        title: 'Activer la gamification',
        description: 'Activez XP et niveaux pour récompenser la progression chapitre par chapitre.',
      },
      {
        title: 'Configurer les bonus',
        description: 'Définissez les points pour complétion rapide, quiz réussis et régularité.',
      },
      {
        title: 'Classement (optionnel)',
        description: 'Affichez un leaderboard pour créer une émulation positive entre apprenants.',
      },
    ],
    sections: [
      {
        heading: 'Mécaniques disponibles',
        bullets: [
          'Points XP par chapitre terminé',
          'Niveaux débloqués par paliers d’XP',
          'Bonus rapidité (complétion dans les temps)',
          'Classement par formation (opt-in)',
        ],
      },
    ],
    tips: [
      'La gamification fonctionne mieux sur des parcours de 4 semaines ou plus.',
      'Communiquez les règles de points dès le premier chapitre.',
    ],
    relatedTopicIds: ['lo-learner-parcours', 'lo-formation-structure'],
  }),

  'lo-formation-satisfaction': detail({
    summary:
      'Collectez un avis NPS ou une note étoiles à la fin de la formation pour mesurer la satisfaction et améliorer vos contenus.',
    steps: [
      {
        title: 'Paramètres de la formation',
        description: 'Ouvrez Paramètres → Satisfaction / Enquête de fin.',
      },
      {
        title: 'Choisir le format',
        description: 'NPS (0–10), étoiles (1–5) ou question ouverte selon votre besoin.',
      },
      {
        title: 'Personnaliser le message',
        description: 'Adaptez le texte d’invitation à votre ton de marque.',
      },
      {
        title: 'Consulter les réponses',
        description: 'Retrouvez les avis dans les analytiques ou l’export des réponses.',
      },
    ],
    sections: [
      {
        heading: 'Bonnes pratiques',
        bullets: [
          'Proposez l’enquête uniquement après complétion du parcours',
          'Gardez le questionnaire court (1 à 3 questions)',
          'Répondez aux avis négatifs pour montrer votre écoute',
        ],
      },
    ],
    tips: ['Un NPS au-dessus de 50 indique une forte recommandation de votre formation.'],
    relatedTopicIds: ['lo-analytics-dashboard', 'lo-learner-progression'],
  }),

  'lo-formation-sequential-lock': detail({
    summary:
      'Le verrouillage séquentiel empêche l’apprenant d’accéder au chapitre suivant tant que le précédent n’est pas terminé (quiz validé, vidéo vue, etc.).',
    steps: [
      {
        title: 'Ouvrir les paramètres player',
        description: 'Depuis la formation, accédez aux paramètres de progression.',
      },
      {
        title: 'Activer le verrouillage',
        description: 'Activez « Progression séquentielle » pour forcer l’ordre des chapitres.',
      },
      {
        title: 'Définir les critères',
        description: 'Choisissez ce qui débloque le suivant : lecture complète, quiz réussi, délai minimum.',
      },
      {
        title: 'Tester en apprenant',
        description: 'Ouvrez le player en mode test pour vérifier les blocages et messages.',
      },
    ],
    sections: [
      {
        heading: 'Quand l’utiliser',
        bullets: [
          'Parcours certifiants ou réglementés',
          'Formations où les prérequis sont essentiels',
          'Programmes avec devoirs obligatoires entre modules',
        ],
      },
    ],
    tips: [
      'Indiquez clairement dans l’introduction que la progression est verrouillée.',
      'Évitez le verrouillage sur des formations courtes de découverte.',
    ],
    relatedTopicIds: ['lo-formation-structure', 'lo-learner-parcours'],
  }),

  'lo-store-vitrine': detail({
    summary:
      'Votre vitrine Store regroupe toutes les formations que vous proposez au public : visuels, descriptions et prix.',
    steps: [
      {
        title: 'Accéder au Store',
        description: 'Menu Marketing ou lien public vers votre vitrine formateur.',
      },
      {
        title: 'Organiser les fiches',
        description: 'Mettez en avant vos best-sellers et vos nouveautés.',
      },
      {
        title: 'Optimiser les visuels',
        description: 'Utilisez des images cohérentes (ratio, style) pour une vitrine professionnelle.',
      },
      {
        title: 'Partager le lien',
        description: 'Copiez l’URL de votre Store pour vos réseaux, e-mails et page créateur.',
      },
    ],
    sections: [
      {
        heading: 'Éléments d’une fiche produit',
        bullets: [
          'Titre accrocheur et sous-titre bénéfice',
          'Image de couverture haute qualité',
          'Description courte (liste) + description longue',
          'Prix, accès gratuit ou promotion',
        ],
      },
    ],
    tips: ['Ajoutez une formation gratuite d’introduction pour convertir les visiteurs.'],
    relatedTopicIds: ['lo-store-prix', 'lo-funnels'],
  }),

  'lo-store-ventes': detail({
    summary:
      'Consultez les achats, attribuez manuellement une formation à un contact et suivez les accès actifs de vos apprenants.',
    steps: [
      {
        title: 'Ouvrir Ventes & apprenants',
        description: 'Menu Store ou Pilotage → liste des achats et accès.',
      },
      {
        title: 'Filtrer les ventes',
        description: 'Triez par date, formation ou statut de paiement.',
      },
      {
        title: 'Attribuer un accès',
        description: 'Offrez une formation à un contact sans passer par le paiement en ligne.',
      },
      {
        title: 'Suivre la progression',
        description: 'Cliquez sur un apprenant pour voir son avancement dans la formation.',
      },
    ],
    sections: [
      {
        heading: 'Actions courantes',
        bullets: [
          'Export des ventes pour comptabilité',
          'Révocation d’accès (remboursement, fin de contrat)',
          'Renvoi de l’e-mail d’accès apprenant',
        ],
      },
    ],
    tips: ['Liez chaque vente à un contact CRM pour un suivi commercial complet.'],
    relatedTopicIds: ['lo-contacts', 'lo-learner-progression'],
  }),

  'lo-store-prix': detail({
    summary:
      'Fixez un prix en euros, proposez un accès gratuit ou une offre promotionnelle. Connectez LyrraPay ou Stripe pour encaisser.',
    steps: [
      {
        title: 'Configurer le paiement',
        description: 'Profil → Paiements : connectez LyrraPay (plan Gratuit/Créateur) ou Stripe (Expert).',
      },
      {
        title: 'Définir le prix',
        description: 'Sur la fiche Store, saisissez le montant TTC ou activez l’accès gratuit.',
      },
      {
        title: 'Promotions',
        description: 'Créez des offres limitées dans le temps via entonnoir ou code promo.',
      },
      {
        title: 'Tester le checkout',
        description: 'Effectuez un achat test pour valider le flux et la confirmation e-mail.',
      },
    ],
    sections: [
      {
        heading: 'Modèles tarifaires',
        bullets: [
          'Achat unique à vie',
          'Accès gratuit (lead magnet)',
          'Bundle à prix réduit (voir Packs formations)',
          'Upsell post-achat via entonnoir',
        ],
      },
    ],
    tips: [
      'Affichez le prix TTC clairement — la transparence augmente la conversion.',
      'Le plan Expert avec Stripe évite la commission LyrraOne sur vos ventes.',
    ],
    relatedTopicIds: ['lo-store-ventes', 'lo-plans'],
  }),

  'lo-contacts': detail({
    summary:
      'La base contacts centralise prospects, clients et apprenants. Utilisez les tags pour segmenter vos campagnes et automatisations.',
    steps: [
      {
        title: 'Ajouter un contact',
        description: 'Création manuelle, formulaire site, achat Store ou import CSV.',
      },
      {
        title: 'Taguer',
        description: 'Appliquez des tags (client, prospect, formation-X) pour segmenter.',
      },
      {
        title: 'Consulter la fiche',
        description: 'Historique d’activité, achats, tags et champs personnalisés.',
      },
      {
        title: 'Exporter',
        description: 'Export CSV pour reporting ou sauvegarde RGPD.',
      },
    ],
    sections: [
      {
        heading: 'Sources de contacts',
        bullets: [
          'Achat sur Store ou entonnoir',
          'Inscription webinaire ou communauté',
          'Formulaire site ou page capture',
          'Import manuel ou API',
        ],
      },
    ],
    tips: [
      'Un tag par formation achetée facilite les relances ciblées.',
      'Respectez le consentement e-mail/SMS avant toute campagne.',
    ],
    relatedTopicIds: ['lo-email', 'lo-automations'],
  }),

  'lo-email': detail({
    summary:
      'Créez des campagnes et séquences avec l’éditeur par blocs LyrraOne : en-tête, texte, bouton, image. Modèles premium disponibles.',
    steps: [
      {
        title: 'Nouvelle campagne',
        description: 'Marketing → E-mails → Créer une campagne ou une séquence.',
      },
      {
        title: 'Composer le message',
        description: 'Glissez des blocs (titre, texte, CTA, image) et prévisualisez.',
      },
      {
        title: 'Choisir l’audience',
        description: 'Tous les contacts, un segment par tags ou une liste importée.',
      },
      {
        title: 'Planifier ou envoyer',
        description: 'Envoi imméat ou programmation ; suivez les ouvertures et clics.',
      },
    ],
    sections: [
      {
        heading: 'Types d’e-mails',
        bullets: [
          'Campagne ponctuelle (newsletter, lancement)',
          'Séquence automatisée (onboarding, relance panier)',
          'E-mails transactionnels (accès formation, confirmation achat)',
        ],
      },
    ],
    tips: [
      'Testez l’affichage sur mobile : la majorité des lectures se font sur smartphone.',
      'Un objet court avec bénéfice clair améliore le taux d’ouverture.',
    ],
    relatedTopicIds: ['lo-automations', 'lo-contacts'],
  }),

  'lo-sms': detail({
    summary:
      'Envoyez des SMS courts à une liste ou un segment. Respectez le consentement, les horaires réglementaires et la limite de caractères.',
    steps: [
      {
        title: 'Configurer SMSFactor',
        description: 'Paramètres → Envoi SMS : token API (Bearer) + crédits SMS sur votre compte SMSFactor.',
      },
      {
        title: 'Rédiger le message',
        description: '160 caractères recommandés ; incluez un lien court si besoin.',
      },
      {
        title: 'Cibler',
        description: 'Segment par tags ou liste de contacts ayant opt-in SMS.',
      },
      {
        title: 'Envoyer',
        description: 'Planifiez en heures ouvrées pour maximiser la lecture.',
      },
    ],
    sections: [
      {
        heading: 'Conformité',
        bullets: [
          'Consentement explicite requis (opt-in)',
          'Lien de désinscription ou STOP selon réglementation',
          'Évitez les envois tard le soir et le week-end',
        ],
      },
    ],
    tips: ['Le SMS est idéal pour les rappels de RDV et les lancements urgents.'],
    relatedTopicIds: ['lo-contacts', 'lo-automations'],
  }),

  'lo-automations': detail({
    summary:
      'Définissez des déclencheurs (inscription, achat, tag ajouté) et des actions (e-mail, tag, notification) sans intervention manuelle.',
    steps: [
      {
        title: 'Créer une automatisation',
        description: 'Marketing → Automatisations → Nouveau scénario.',
      },
      {
        title: 'Choisir le déclencheur',
        description: 'Nouveau contact, achat formation, tag, date ou événement custom.',
      },
      {
        title: 'Ajouter des actions',
        description: 'Envoyer e-mail, attendre X jours, ajouter un tag, créer une tâche CRM.',
      },
      {
        title: 'Activer et surveiller',
        description: 'Consultez les exécutions et les contacts en cours de parcours.',
      },
    ],
    sections: [
      {
        heading: 'Exemples de scénarios',
        bullets: [
          'Bienvenue : e-mail J0 + relance J3 si pas d’achat',
          'Post-achat : accès formation + enquête J+7',
          'Panier abandonné : rappel e-mail + deal CRM',
        ],
      },
    ],
    tips: [
      'Limitez à 3 automatisations actives en plan Gratuit — priorisez les plus rentables.',
      'Testez avec votre propre contact avant activation.',
    ],
    relatedTopicIds: ['lo-email', 'lo-funnels'],
  }),

  'lo-funnels': detail({
    summary:
      'Enchaînez pages de capture, checkout et page merci pour convertir un visiteur en acheteur de formation.',
    steps: [
      {
        title: 'Créer un entonnoir',
        description: 'Marketing → Entonnoirs → Nouveau, nommez votre campagne.',
      },
      {
        title: 'Ajouter des étapes',
        description: 'Page capture, page vente, checkout, page merci — réordonnez par glisser-déposer.',
      },
      {
        title: 'Lier une offre',
        description: 'Associez une formation Store ou un produit physique à l’étape de paiement.',
      },
      {
        title: 'Publier et partager',
        description: 'Copiez l’URL publique du funnel pour vos publicités et e-mails.',
      },
    ],
    sections: [
      {
        heading: 'Bonnes pratiques',
        bullets: [
          'Une promesse claire sur la page capture',
          'Preuve sociale (témoignages) sur la page vente',
          'Checkout simplifié (peu de champs)',
          'Page merci avec prochaine étape (upsell, communauté)',
        ],
      },
    ],
    tips: ['Analysez le taux de conversion par étape dans les analytiques entonnoir.'],
    relatedTopicIds: ['lo-funnel-order-bumps', 'lo-funnel-upsell', 'lo-site'],
  }),

  'lo-funnel-order-bumps': detail({
    summary:
      'Proposez jusqu’à 3 offres complémentaires sur la page de paiement : formations numériques ou produits physiques en un clic.',
    steps: [
      {
        title: 'Ouvrir l’étape checkout',
        description: 'Dans votre entonnoir, sélectionnez l’étape de paiement.',
      },
      {
        title: 'Ajouter un order bump',
        description: 'Choisissez une formation ou un produit physique complémentaire.',
      },
      {
        title: 'Rédiger le pitch',
        description: 'Titre court + bénéfice (ex. « Ajoutez le workbook PDF pour 19 € »).',
      },
      {
        title: 'Tester le panier',
        description: 'Vérifiez que l’offre s’ajoute au total sans quitter le checkout.',
      },
    ],
    sections: [
      {
        heading: 'Conseils de conversion',
        bullets: [
          'Prix inférieur à l’offre principale (10–30 % du panier)',
          'Complémentaire, pas concurrente à l’offre principale',
          'Maximum 3 bumps pour ne pas surcharger',
        ],
      },
    ],
    tips: ['Les order bumps convertissent mieux que les upsells post-achat sur les offres impulsives.'],
    relatedTopicIds: ['lo-funnels', 'lo-funnel-upsell'],
  }),

  'lo-funnel-upsell': detail({
    summary:
      'Sur la page merci, proposez une offre complémentaire avec achat express Stripe en 1 clic si l’acheteur est connecté.',
    steps: [
      {
        title: 'Configurer la page merci',
        description: 'Dans l’entonnoir, ouvrez l’étape « Merci » après le paiement.',
      },
      {
        title: 'Ajouter l’upsell',
        description: 'Sélectionnez une formation ou offre à prix réduit ou premium.',
      },
      {
        title: 'Rédiger l’argumentaire',
        description: 'Mettez en avant la valeur ajoutée immédiate (« Passez au niveau expert »).',
      },
      {
        title: 'Option downsell',
        description: 'Si l’upsell est refusé, proposez une alternative à moindre coût.',
      },
    ],
    sections: [
      {
        heading: 'Fonctionnement technique',
        bullets: [
          'Paiement 1 clic via Stripe si carte déjà enregistrée',
          'Accès formation débloqué automatiquement après achat',
          'Tracking des conversions upsell dans les analytiques',
        ],
      },
    ],
    tips: [
      'L’upsell fonctionne mieux juste après un achat satisfait (page merci).',
      'Proposez une réduction limitée dans le temps pour créer l’urgence.',
    ],
    relatedTopicIds: ['lo-funnels', 'lo-funnel-order-bumps'],
  }),

  'lo-site': detail({
    summary:
      'Le site formateur est un mini-site vitrine sans code : accueil, à propos, catalogue formations, blog et packs.',
    steps: [
      {
        title: 'Ouvrir l’éditeur site',
        description: 'Marketing → Site web → Personnaliser les pages.',
      },
      {
        title: 'Choisir un thème',
        description: 'Sélectionnez parmi 19+ modèles et adaptez couleurs et typographie.',
      },
      {
        title: 'Composer les pages',
        description: 'Ajoutez des blocs (héro, témoignages, FAQ, catalogue, contact).',
      },
      {
        title: 'Publier',
        description: 'Votre site est accessible sur votre sous-domaine LyrraOne ou domaine personnalisé.',
      },
    ],
    sections: [
      {
        heading: 'Blocs utiles',
        bullets: [
          'Catalogue formations (lien Store)',
          'Packs formations (bundles)',
          'Formulaire contact',
          'Blog et SEO',
          'Témoignages et FAQ',
        ],
      },
    ],
    tips: [
      'Connectez un domaine personnalisé pour renforcer votre marque.',
      'Ajoutez une page « À propos » avec votre histoire et vos preuves sociales.',
    ],
    relatedTopicIds: ['lo-formation-bundles', 'lo-creator-page'],
  }),

  'lo-formation-bundles': detail({
    summary:
      'Regroupez plusieurs formations à prix réduit et affichez-les sur votre site ou Store via le bloc « Packs ».',
    steps: [
      {
        title: 'Créer un pack',
        description: 'Marketing → Packs formations → Nouveau pack.',
      },
      {
        title: 'Sélectionner les formations',
        description: 'Ajoutez 2 à 5 formations complémentaires au bundle.',
      },
      {
        title: 'Fixer le prix bundle',
        description: 'Proposez 20–40 % de réduction par rapport à l’achat séparé.',
      },
      {
        title: 'Afficher sur le site',
        description: 'Insérez le bloc Packs sur votre page d’accueil ou offres.',
      },
    ],
    sections: [
      {
        heading: 'Stratégie bundle',
        bullets: [
          'Pack découverte (entrée de gamme)',
          'Pack complet (toutes vos formations)',
          'Pack thématique (ex. « Marketing digital »)',
        ],
      },
    ],
    tips: ['Mettez en avant l’économie réalisée par rapport au prix unitaire.'],
    relatedTopicIds: ['lo-store-vitrine', 'lo-site'],
  }),

  'lo-creator-page': detail({
    summary:
      'Page bio link unique : tous vos liens (Store, RDV, réseaux, formations) sur une URL partageable.',
    steps: [
      {
        title: 'Ouvrir Page créateur',
        description: 'Marketing → Page créateur & bio link.',
      },
      {
        title: 'Personnaliser',
        description: 'Photo, titre, bio courte et liens vers vos offres.',
      },
      {
        title: 'Ajouter des liens',
        description: 'Store, prise de RDV, réseaux sociaux, lead magnet gratuit.',
      },
      {
        title: 'Partager',
        description: 'Utilisez l’URL courte dans votre bio Instagram, LinkedIn, signature e-mail.',
      },
    ],
    sections: [
      {
        heading: 'Idées de liens',
        bullets: [
          'Formation gratuite (lead magnet)',
          'Page de vente principale',
          'Calendrier de RDV coaching',
          'Communauté ou newsletter',
        ],
      },
    ],
    tips: ['Mettez votre offre principale en premier lien pour maximiser les clics.'],
    relatedTopicIds: ['lo-site', 'lo-booking'],
  }),

  'lo-crm': detail({
    summary:
      'Pipeline visuel des opportunités : kanban et vue liste, étapes personnalisables, deals auto sur checkout abandonné.',
    steps: [
      {
        title: 'Ouvrir le CRM',
        description: 'Marketing → Pipeline CRM.',
      },
      {
        title: 'Configurer les étapes',
        description: 'Nouveau lead → Contacté → Proposition → Négociation → Gagné / Perdu.',
      },
      {
        title: 'Créer un deal',
        description: 'Associez un contact, un montant estimé et une formation cible.',
      },
      {
        title: 'Déplacer les cartes',
        description: 'Glissez les deals entre colonnes au fil de vos échanges.',
      },
    ],
    sections: [
      {
        heading: 'Automatisations CRM',
        bullets: [
          'Deal auto sur checkout abandonné',
          'Rappel si deal inactif depuis X jours',
          'Lien vers historique contact et achats',
        ],
      },
    ],
    tips: ['Revoyez votre pipeline chaque lundi pour relancer les deals bloqués.'],
    relatedTopicIds: ['lo-contacts', 'lo-funnels'],
  }),

  'lo-affiliate': detail({
    summary:
      'Créez un programme d’affiliation : liens trackés, commissions et tableau de bord pour vos partenaires promoteurs.',
    steps: [
      {
        title: 'Activer l’affiliation',
        description: 'Marketing → Programme d’affiliation → Paramètres.',
      },
      {
        title: 'Définir les commissions',
        description: 'Pourcentage par vente ou montant fixe par formation.',
      },
      {
        title: 'Inviter des affiliés',
        description: 'Envoyez des liens d’inscription à vos partenaires.',
      },
      {
        title: 'Suivre les performances',
        description: 'Consultez clics, conversions et commissions dues.',
      },
    ],
    sections: [
      {
        heading: 'Bonnes pratiques',
        bullets: [
          'Fournissez des visuels et textes prêts à l’emploi',
          'Commissions attractives (20–40 % sur produits digitaux)',
          'Paiement régulier des affiliés actifs',
        ],
      },
    ],
    tips: ['Les anciens apprenants satisfaits font d’excellents affiliés.'],
    relatedTopicIds: ['lo-store-ventes', 'lo-funnels'],
  }),

  'lo-booking': detail({
    summary:
      'Proposez des créneaux de rendez-vous (coaching, appel découverte) avec synchronisation Google Calendar et rappels e-mail.',
    steps: [
      {
        title: 'Créer un type de RDV',
        description: 'Marketing → Prise de RDV → Nouveau type (durée, lieu visio).',
      },
      {
        title: 'Définir les disponibilités',
        description: 'Plages horaires récurrentes et fuseau horaire.',
      },
      {
        title: 'Connecter Google Calendar',
        description: 'Synchronisez pour éviter les doubles réservations.',
      },
      {
        title: 'Partager le lien',
        description: 'Ajoutez le lien sur votre page créateur, site et e-mails.',
      },
    ],
    sections: [
      {
        heading: 'Types de rendez-vous',
        bullets: [
          'Appel découverte gratuit (15–30 min)',
          'Session coaching payante',
          'Suivi post-formation',
        ],
      },
    ],
    tips: ['Proposez 2 à 3 créneaux types pour simplifier le choix du prospect.'],
    relatedTopicIds: ['lo-creator-page', 'lo-crm'],
  }),

  'lo-communities': detail({
    summary:
      'Espace communautaire pour vos apprenants : discussions, salles thématiques, annonces et engagement entre sessions.',
    steps: [
      {
        title: 'Créer une communauté',
        description: 'Marketing → Communautés → Nouvelle communauté.',
      },
      {
        title: 'Définir l’accès',
        description: 'Gratuite, liée à une formation achetée ou payante séparément.',
      },
      {
        title: 'Personnaliser',
        description: 'Charte, bannière, règles de modération et salles de discussion.',
      },
      {
        title: 'Animer',
        description: 'Publiez des posts d’accueil, défis hebdomadaires et répondez aux questions.',
      },
    ],
    sections: [
      {
        heading: 'Fonctionnalités',
        bullets: [
          'Fil de discussion et commentaires',
          'Salles thématiques par sujet',
          'Likes et engagement',
          'Statistiques publiques (membres, activité)',
        ],
      },
    ],
    tips: ['Une communauté active réduit le churn et augmente les ventes de formations avancées.'],
    relatedTopicIds: ['lo-learner-parcours', 'lo-webinars'],
  }),

  'lo-webinars': detail({
    summary:
      'Webinaire evergreen : enregistrement diffusé en continu avec page d’inscription intégrée pour générer des leads.',
    steps: [
      {
        title: 'Créer un webinaire',
        description: 'Marketing → Webinaires → Nouveau webinaire evergreen.',
      },
      {
        title: 'Uploader la vidéo',
        description: 'Enregistrement de votre présentation (45–60 min recommandé).',
      },
      {
        title: 'Page d’inscription',
        description: 'Formulaire capture e-mail + rappel automatique.',
      },
      {
        title: 'Offre de fin',
        description: 'CTA vers votre formation ou entonnoir à la fin du replay.',
      },
    ],
    sections: [
      {
        heading: 'Parcours type',
        bullets: [
          'Inscription → e-mail de confirmation',
          'Accès au replay (simulation live ou direct)',
          'Séquence e-mail post-webinaire',
        ],
      },
    ],
    tips: ['Simulez un « live » avec compte à rebours pour augmenter l’urgence.'],
    relatedTopicIds: ['lo-funnels', 'lo-contacts'],
  }),

  'lo-physical-products': detail({
    summary:
      'Vendez des produits physiques (livres, kits, goodies) en complément de vos formations digitales, avec gestion des variantes et livraison.',
    steps: [
      {
        title: 'Créer un produit',
        description: 'Marketing → Produits physiques → Nouveau produit.',
      },
      {
        title: 'Variantes et stock',
        description: 'Tailles, couleurs, SKU et quantités disponibles.',
      },
      {
        title: 'Livraison',
        description: 'Zones de livraison, frais de port et délais indicatifs.',
      },
      {
        title: 'Vendre',
        description: 'Ajoutez au Store, site, entonnoir (order bump) ou bundle.',
      },
    ],
    sections: [
      {
        heading: 'Intégrations',
        bullets: [
          'Checkout entonnoir avec produit physique',
          'Order bumps sur formations digitales',
          'Suivi des commandes et statuts d’expédition',
        ],
      },
    ],
    tips: ['Combinez livre physique + formation digitale en pack pour augmenter la valeur perçue.'],
    relatedTopicIds: ['lo-store-vitrine', 'lo-funnel-order-bumps'],
  }),

  'lo-learner-parcours': detail({
    summary:
      'L’apprenant accède à ses formations depuis /learner : reprise au dernier chapitre, navigation linéaire, quiz et certificats.',
    steps: [
      {
        title: 'Connexion apprenant',
        description: 'L’apprenant se connecte ou utilise le lien magique reçu par e-mail.',
      },
      {
        title: 'Tableau de bord apprenant',
        description: 'Liste des formations achetées ou attribuées avec progression.',
      },
      {
        title: 'Reprise de lecture',
        description: 'Le player ouvre automatiquement au dernier chapitre consulté.',
      },
      {
        title: 'Complétion',
        description: 'Quiz, devoirs et certificat selon la configuration de la formation.',
      },
    ],
    sections: [
      {
        heading: 'Expérience apprenant',
        bullets: [
          'Navigation chapitre par chapitre',
          'Barre de progression globale',
          'Gamification (XP, niveaux) si activée',
          'Enquête de satisfaction en fin de parcours',
        ],
      },
    ],
    tips: ['Envoyez un e-mail de bienvenue avec le lien direct vers la première leçon.'],
    relatedTopicIds: ['lo-learner-progression', 'lo-formation-structure'],
  }),

  'lo-learner-progression': detail({
    summary:
      'Suivez le pourcentage de complétion, le temps d’apprentissage et l’activité hebdomadaire de chaque apprenant.',
    steps: [
      {
        title: 'Ouvrir la progression',
        description: 'Pilotage → Apprenants ou depuis une formation → Liste des inscrits.',
      },
      {
        title: 'Filtrer',
        description: 'Par formation, statut (en cours, terminé, inactif) ou période.',
      },
      {
        title: 'Analyser',
        description: 'Temps passé, chapitres complétés, scores quiz.',
      },
      {
        title: 'Relancer',
        description: 'Identifiez les inactifs et envoyez une campagne de réengagement.',
      },
    ],
    sections: [
      {
        heading: 'Indicateurs clés',
        bullets: [
          'Taux de complétion par formation',
          'Temps moyen par chapitre',
          'Taux de réussite aux quiz',
          'Apprenants inactifs depuis 7+ jours',
        ],
      },
    ],
    tips: ['Un taux de complétion sous 40 % signale souvent un contenu trop long ou peu engageant.'],
    relatedTopicIds: ['lo-analytics-dashboard', 'lo-email'],
  }),

  'medias-bibliotheque': detail({
    summary:
      'Importez une fois, réutilisez partout : vidéos, audios et images dans formations, e-mails, site et entonnoirs.',
    steps: [
      {
        title: 'Ouvrir la bibliothèque',
        description: 'Menu Médias → Bibliothèque.',
      },
      {
        title: 'Importer',
        description: 'Glissez-déposez ou sélectionnez vos fichiers (vérifiez les quotas de stockage).',
      },
      {
        title: 'Organiser',
        description: 'Renommez et recherchez par type pour retrouver rapidement un asset.',
      },
      {
        title: 'Réutiliser',
        description: 'Sélectionnez depuis l’éditeur formation, e-mail ou site.',
      },
    ],
    sections: [
      {
        heading: 'Formats supportés',
        bullets: [
          'Vidéo (MP4, streaming optimisé)',
          'Audio (MP3, WAV)',
          'Images (JPG, PNG, WebP)',
          'Documents PDF',
        ],
      },
    ],
    tips: [
      'Compressez vos vidéos avant upload pour accélérer le chargement player.',
      'Utilisez des noms de fichier explicites (formation-module1-intro.mp4).',
    ],
    relatedTopicIds: ['lo-formation-contenu', 'lo-podcasts'],
  }),

  'lo-podcasts': detail({
    summary:
      'Créez des épisodes audio avec couverture et métadonnées ; diffusez-les en complément de vos formations ou en offre autonome.',
    steps: [
      {
        title: 'Nouveau podcast',
        description: 'Création → Podcasts → Créer un podcast.',
      },
      {
        title: 'Ajouter des épisodes',
        description: 'Upload audio, titre, description et image de couverture par épisode.',
      },
      {
        title: 'Publier',
        description: 'Rendez le podcast accessible sur le Store ou en bonus formation.',
      },
      {
        title: 'Partager',
        description: 'Lien public, intégration page créateur ou séquence e-mail.',
      },
    ],
    sections: [
      {
        heading: 'Cas d’usage',
        bullets: [
          'Complément audio d’une formation écrite',
          'Série thématique standalone',
          'Replay de webinaires en format podcast',
        ],
      },
    ],
    tips: ['Des épisodes de 15–25 minutes ont le meilleur taux d’écoute complète.'],
    relatedTopicIds: ['medias-bibliotheque', 'lo-formation-contenu'],
  }),

  'lo-analytics-dashboard': detail({
    summary:
      'Vue consolidée : ventes, inscriptions, taux de complétion et performance des campagnes marketing.',
    steps: [
      {
        title: 'Ouvrir les analytiques',
        description: 'Pilotage → Analytiques ou Tableau de bord analytique.',
      },
      {
        title: 'Choisir la période',
        description: '7 jours, 30 jours, trimestre ou plage personnalisée.',
      },
      {
        title: 'Explorer les graphiques',
        description: 'Ventes, nouveaux contacts, engagement formations, performance e-mails.',
      },
      {
        title: 'Exporter',
        description: 'Téléchargez les données pour reporting externe si besoin.',
      },
    ],
    sections: [
      {
        heading: 'Métriques principales',
        bullets: [
          'Chiffre d’affaires Store et entonnoirs',
          'Nouveaux contacts et taux de conversion',
          'Complétion moyenne par formation',
          'Ouvertures et clics e-mail',
        ],
      },
    ],
    tips: ['Comparez toujours deux périodes pour détecter les tendances, pas seulement les totaux.'],
    relatedTopicIds: ['lo-learner-progression', 'lo-store-ventes'],
  }),

  'support-faq': detail({
    summary:
      'En cas de blocage : videz le cache, testez en navigation privée, vérifiez que la formation est publiée et que le paiement est confirmé.',
    sections: [
      {
        heading: 'Lecteur formation ne charge pas',
        bullets: [
          'Hard refresh : Ctrl+F5 (Windows) ou Cmd+Shift+R (Mac)',
          'Tester en navigation privée sans extensions',
          'Vérifier que la formation est publiée (pas en brouillon)',
          'Contrôler la connexion réseau et désactiver VPN',
        ],
      },
      {
        heading: 'E-mail non reçu',
        bullets: [
          'Vérifier dossier spam / promotions',
          'Confirmer l’adresse e-mail du contact',
          'Vérifier les quotas d’envoi du plan',
          'Tester avec votre propre adresse',
        ],
      },
      {
        heading: 'Paiement en attente ou échoué',
        bullets: [
          'Confirmer que Stripe ou LyrraPay est connecté',
          'Vérifier le statut dans Ventes & apprenants',
          'Demander à l’acheteur de réessayer avec une autre carte',
        ],
      },
      {
        heading: 'Problème d’accès apprenant',
        bullets: [
          'Vérifier que l’accès est bien attribué (Store ou manuel)',
          'Renvoyer l’e-mail d’accès depuis la fiche contact',
          'Contrôler que le compte apprenant utilise le bon e-mail',
        ],
      },
    ],
    tips: [
      'Notez l’URL exacte, l’heure et les étapes avant de contacter le support.',
      'Une capture d’écran accélère le diagnostic.',
    ],
    relatedTopicIds: ['support-contact'],
  }),

  'support-contact': detail({
    summary:
      'Contactez le support LyrraOne via le chat intégré avec une capture d’écran, l’URL concernée et les étapes pour reproduire le problème.',
    steps: [
      {
        title: 'Ouvrir le chat support',
        description: 'Icône bulle en bas à droite ou onglet Aide du chat.',
      },
      {
        title: 'Décrire le problème',
        description: 'Module concerné, message d’erreur exact, comportement attendu vs observé.',
      },
      {
        title: 'Joindre des éléments',
        description: 'Capture d’écran, URL de la page, identifiant formation si pertinent.',
      },
      {
        title: 'Suivre la résolution',
        description: 'Répondez aux questions du support pour accélérer le diagnostic.',
      },
    ],
    sections: [
      {
        heading: 'Informations utiles',
        bullets: [
          'Navigateur et version (Chrome, Safari, Firefox)',
          'Compte formateur (e-mail de connexion)',
          'Plan LyrraOne actuel',
          'Heure approximative du incident',
        ],
      },
    ],
    tips: [
      'Consultez d’abord les fiches Problèmes fréquents — la solution y est souvent.',
      'Pour les urgences commerciales (paiement bloqué), précisez « urgent » dans le message.',
    ],
    relatedTopicIds: ['support-faq'],
  }),
};
